import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    Grid,
    InputAdornment,
    makeStyles,
    TextField,
    Typography,
} from "@material-ui/core";
import {Controller, useForm} from "react-hook-form";
import {useLoading} from "../../../../util/LoadingProvider";
import apiHelper from "../../../../util/ApiHelper";
import {toast} from "react-toastify";

const defaultFormValues = {
    confirmationEmailName: "",
    confirmationEmailAddress: "",
    shippingCost: 0,
    renewalDiscount: 0
};

const useStyles = makeStyles((theme) => ({
    textInput: {
        width: "100%",
        marginBottom: "12px",

        "& input": {
            fontSize: "14px",
        },
    },
    content: {
        "& .ck-editor__editable_inline": {
            minHeight: "362px",
        },
    },
    buttonGroup: {
        marginTop: "40px",
        textAlign: "left",
        "& button": {
            color: "#306F9D",
            fontSize: "13px",
            textTransform: "inherit",
        },
    },
    box: {
        padding: 0,
    },
    input: {
        width: "100%",
    },
}));

export default function MemberConfigurationPage({
                                                    disabled,
                                                    ...props
                                                }) {
    const [defaultValues, setDefaultValues] = useState(defaultFormValues);
    const {setLoading} = useLoading();

    const classes = useStyles();

    const {
        control,
        handleSubmit,
        getValues,
        setValue,
        reset,
    } = useForm({
        defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
    });

    const onSubmit = (value) => {
        updateMemberConfiguration(value);
    };

    const toCamelCase = (dotString) => {
        const fragments = dotString.split('.');
        for (let i = 1; i < fragments.length; i++) {
            fragments[i] = fragments[i] ? fragments[i][0].toUpperCase() + fragments[i].substring(1, fragments[i].length) : fragments[i];
        }
        return fragments.join('');
    }

    const toDotString = (camelCaseString) => {
        let dotString = '';
        for (let i = 0; i < camelCaseString.length; i++) {
            const letter = camelCaseString.substring(i, i + 1);
            if (letter === letter.toUpperCase() && letter !== letter.toLowerCase()) {
                dotString += `.${letter.toLowerCase()}`;
            } else {
                dotString += letter;
            }
        }
        return dotString;
    }

    const getMemberConfiguration = React.useCallback(async () => {
        setLoading(true);
        try {
            const result = await apiHelper.hkiff.getMembershipConfig();
            const preData = result.data;
            const dataObject = {};
            preData.forEach((item) => {
                dataObject[toCamelCase(item.key)] = item.value;
            });
            const data = {
                ...dataObject,
                confirmationEmailAddress: dataObject.confirmationEmailAddress.split('@')[0]
            };
            setDefaultValues(data);
            reset(data);
        } catch (err) {
            console.log(err);
        }
        setLoading(false);
    }, [reset, setLoading]);

    const updateMemberConfiguration = async (value) => {
        try {
            const data = Object.keys(value).map((key) => {
                return {
                    key: toDotString(key),
                    value: value[key]
                        ?
                        value[key]
                        : (
                            (key === 'shippingCost' || key === 'renewalDiscount')
                                ?
                                0
                                :
                                ''
                        )
                }
            })
            const result = await apiHelper.hkiff.updateMembershipConfig(data);
            if (result.status === 200) {
                toast.dark("Your changes are saved.");
                getMemberConfiguration();
            }
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        getMemberConfiguration();
    }, [getMemberConfiguration]);

    return (
        <div className={classes.content}>
            <Box width="100%">
                <Typography variant="h4" gutterBottom>
                    Member > Configuration
                </Typography>
            </Box>
            <form onSubmit={handleSubmit(onSubmit)}>
                <fieldset disabled={disabled}
                          className="fieldset">
                    <Grid container xs={6} direction="row"
                          spacing={3}>
                        <Grid container item xs={12}>
                            <Controller
                                name="confirmationEmailName"
                                control={control}
                                render={({
                                             field,
                                             fieldState: {error}
                                         }) => (
                                    <TextField
                                        margin="dense"
                                        variant="outlined"
                                        error={!!error}
                                        label="Sender Name"
                                        defaultValue={field.name}
                                        fullWidth
                                        helperText={
                                            error
                                                ? error.message
                                                : "The name is how it displayed in your contacts' inboxes."
                                        }
                                        {...field}
                                    />
                                )}
                                rules={{required: "Name required"}}
                            />
                        </Grid>
                        <Grid container item xs={12}>
                            <Controller
                                name="confirmationEmailAddress"
                                control={control}
                                render={({
                                             field,
                                             fieldState: {error}
                                         }) => (
                                    <TextField
                                        margin="dense"
                                        variant="outlined"
                                        error={!!error}
                                        label="Sender Address"
                                        defaultValue={field.address}
                                        fullWidth
                                        helperText={
                                            error
                                                ? error.message
                                                : "The sender address your contacts will see."
                                        }
                                        {...field}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment
                                                    position="end">
                                                    @HKIFF.org.hk
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                                rules={{required: "Address required"}}
                            />
                        </Grid>

                        <Grid container item xs={12}>
                            <Controller
                                name="shippingCost"
                                control={control}
                                render={({
                                             field: {
                                                 value,
                                                 ...rest
                                             },
                                             fieldState: {error},
                                         }) => (
                                    <TextField
                                        label="Shipping Cost"
                                        variant="outlined"
                                        margin="dense"
                                        className={classes.textInput}
                                        type="number"
                                        InputProps={{
                                            inputProps: {
                                                min: 0,
                                            }
                                        }}
                                        fullWidth
                                        {...rest}
                                        value={value}
                                        error={!!error}
                                        helperText={error ? error.message : ""}
                                        onWheel={(e) => e.target.blur()}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid container item xs={12}>
                            <Controller
                                name="renewalDiscount"
                                control={control}
                                render={({
                                             field: {
                                                 value,
                                                 ...rest
                                             },
                                             fieldState: {error},
                                         }) => (
                                    <TextField
                                        label="Renewal Discount"
                                        variant="outlined"
                                        margin="dense"
                                        className={classes.textInput}
                                        type="number"
                                        InputProps={{
                                            inputProps: {
                                                min: 0,
                                                step: 0.1
                                            }
                                        }}
                                        fullWidth
                                        {...rest}
                                        value={value}
                                        error={!!error}
                                        helperText={error ? error.message : ""}
                                        onWheel={(e) => e.target.blur()}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>


                    {disabled || (
                        <div
                            className={classes.buttonGroup}>
                            <Button
                                type="submit"
                                variant="contained"
                                disableElevation
                                style={{
                                    color: "white",
                                    background: "#306F9D",
                                    marginRight: "5px",
                                    padding: "9px 16px",
                                }}
                            >
                                Save Changes
                            </Button>
                        </div>
                    )}
                </fieldset>
            </form>
        </div>
    );
}
